'use client';

import type { UseChatHelpers } from '@ai-sdk/react';
import { processDataStream, type UIMessage } from 'ai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '@/lib/config';
import { sessionFetcher } from '@/lib/api-client';
import { convertToUIMessages } from '@/lib/session-utils';
import { fetchWithErrorHandlers, generateUUID } from '@/lib/utils';
import { telemetry } from '@/lib/telemetry';
import { createChannelLogger } from '@/lib/debug-log';

// Shared with chat.tsx + alerts/[id]/page.tsx so all handoff-related
// events land in one file (frontend_edit_alert_handoff.txt). Logger
// no-ops off-localhost so this has zero production cost.
const autoResumeHandoffLog = createChannelLogger('edit_alert_handoff');

type RecoveryMode = 'idle' | 'polling' | 'streaming';

interface RequestStatusResponse {
  status: string;
  request_id: string | null;
}

interface StoredResumeState {
  requestId: string;
  lastChunkId: string | null;
  tailMessages: UIMessage[];
  updatedAt: number;
}

export interface UseAutoResumeParams {
  autoResume: boolean;
  chatId: string;
  userId?: string;
  replayId?: string;
  messages: UIMessage[];
  status: UseChatHelpers['status'];
  data: UseChatHelpers['data'];
  setData: UseChatHelpers['setData'];
  setMessages: UseChatHelpers['setMessages'];
  onRecoveryFailed?: () => void;
  // Ref set true by the host when it has just called append() with an
  // optimistic user message whose server-side persistence hasn't landed yet.
  // When true, the recovery hook MUST NOT overwrite `messages` with a
  // freshly-fetched DB snapshot — doing so would wipe the optimistic user
  // message (see the edit_alert handoff bug where the user's message
  // flashed in, vanished, then re-appeared when the agent reply streamed).
  // The host is responsible for clearing the ref once the agent response
  // arrives (typically when status transitions back to 'ready').
  skipDbHydrationRef?: React.MutableRefObject<boolean>;
}

export interface UseAutoResumeResult {
  isRecovering: boolean;
  isResumeStreaming: boolean;
  stopResuming: () => void;
  recoverNow: () => void;
}

const RESUME_STORAGE_PREFIX = 'xynth:durable-resume:';
const POLL_INTERVAL_MS = 2000;
const DRIP_WORD_INTERVAL_MS = 25;
// Risk B (reliability v2): hard cap on the polling-fallback loop. Without it,
// a request that never flips off "running" server-side (stuck/abandoned)
// would poll the DB every 2s forever. After this budget we finalize from the
// DB snapshot and stop — turning a potential infinite loop into a bounded,
// observable timeout (stream.poll_timeout).
//
// 2026-07-10: raised 3min → 30min. The 3-minute budget was killing recovery
// 69s before the agent finished (prod session a9341155: poll_timeout at
// 22:23:53, agent request_end at 22:25:02 — user saw a dead chat until manual
// reload). The poll loop ALREADY exits promptly when the backend reports the
// request is no longer running (fetchRequestStatus each iteration), so this
// cap only exists for zombie requests stuck on "running" forever — 30min
// bounds that pathological case without betraying healthy long agent runs
// (fund/deep-dive sessions legitimately run 10-25 min).
const MAX_POLL_DURATION_MS = 1_800_000;
// Fix #5 (reliability v2): bounded silent DB re-hydrate when the whole
// recovery pipeline throws. Backend always finishes + persists the answer, so
// re-fetching usually surfaces it with zero user action. Capped + backed off
// so a persistent network failure can never loop.
const AUTO_REHYDRATE_MAX_ATTEMPTS = 3;
const AUTO_REHYDRATE_BACKOFF_MS = 1000;
// TEMPORARY: set to true to force polling fallback for testing smooth animation
const FORCE_POLLING_FALLBACK = false;

interface RecoveryTelemetryState {
  attempt: number;
  requestId: string | null;
  trigger: 'mount' | 'live_drop' | null;
  startedAtMs: number | null;
  startedAtIso: string | null;
  mode: 'polling' | 'streaming' | null;
  usedPollingFallback: boolean;
  fallbackReason: string | null;
  resumeChunkOffsetsMs: number[];
  resumeHeartbeatOffsetsMs: number[];
  lastResumeChunkId: string | null;
  lastResumeActivityAtMs: number | null;
  maxResumeGapMs: number;
  timeToFirstResumeChunkMs: number | null;
  pollStartedAtMs: number | null;
  pollStartedAtIso: string | null;
  pollProgressOffsetsMs: number[];
  pollMessageCounts: number[];
  pollAssistantTextLengths: number[];
  pollIterations: number;
  pollUpdates: number;
}

function createRecoveryTelemetryState(
  attempt = 0,
): RecoveryTelemetryState {
  return {
    attempt,
    requestId: null,
    trigger: null,
    startedAtMs: null,
    startedAtIso: null,
    mode: null,
    usedPollingFallback: false,
    fallbackReason: null,
    resumeChunkOffsetsMs: [],
    resumeHeartbeatOffsetsMs: [],
    lastResumeChunkId: null,
    lastResumeActivityAtMs: null,
    maxResumeGapMs: 0,
    timeToFirstResumeChunkMs: null,
    pollStartedAtMs: null,
    pollStartedAtIso: null,
    pollProgressOffsetsMs: [],
    pollMessageCounts: [],
    pollAssistantTextLengths: [],
    pollIterations: 0,
    pollUpdates: 0,
  };
}

function describeError(error: unknown) {
  if (error instanceof Error) {
    return {
      error_type: error.name || 'Error',
      error_message: error.message.slice(0, 500),
    };
  }

  return {
    error_type: typeof error,
    error_message: String(error).slice(0, 500),
  };
}

function getAssistantTextLength(messages: UIMessage[]) {
  return extractAssistantTextInfo(messages)?.fullText.length ?? 0;
}

function getStorageKey(chatId: string) {
  return `${RESUME_STORAGE_PREFIX}${chatId}`;
}

function reviveMessages(messages: any[]): UIMessage[] {
  return messages.map((message: any) => ({
    ...message,
    createdAt: message?.createdAt ? new Date(message.createdAt) : undefined,
  })) as UIMessage[];
}

function readStoredResumeState(chatId: string): StoredResumeState | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(getStorageKey(chatId));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.requestId) return null;

    return {
      requestId: parsed.requestId,
      lastChunkId:
        typeof parsed.lastChunkId === 'string' ? parsed.lastChunkId : null,
      tailMessages: reviveMessages(
        Array.isArray(parsed.tailMessages) ? parsed.tailMessages : [],
      ),
      updatedAt:
        typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

function writeStoredResumeState(chatId: string, state: StoredResumeState) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(getStorageKey(chatId), JSON.stringify(state));
  } catch {
    try {
      window.localStorage.removeItem(getStorageKey(chatId));
    } catch {}
  }
}

function clearStoredResumeState(chatId: string) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(getStorageKey(chatId));
  } catch {}
}

function findRequestStartIndex(messages: UIMessage[], requestId: string) {
  return messages.findIndex(
    (message) => message.role === 'user' && message.id === requestId,
  );
}

function sliceRequestTail(messages: UIMessage[], requestId: string) {
  const requestStartIndex = findRequestStartIndex(messages, requestId);
  return requestStartIndex === -1 ? messages : messages.slice(requestStartIndex);
}

function mergeRequestTail(
  baseMessages: UIMessage[],
  requestId: string,
  tailMessages: UIMessage[],
) {
  const requestStartIndex = findRequestStartIndex(baseMessages, requestId);
  return requestStartIndex === -1
    ? tailMessages
    : [...baseMessages.slice(0, requestStartIndex), ...tailMessages];
}

function getMessageScore(messages: UIMessage[]) {
  try {
    return JSON.stringify(messages).length;
  } catch {
    return messages.length;
  }
}

function choosePreferredTail(
  dbTailMessages: UIMessage[],
  snapshotTailMessages: UIMessage[],
) {
  return getMessageScore(snapshotTailMessages) > getMessageScore(dbTailMessages)
    ? { tailMessages: snapshotTailMessages, source: 'snapshot' as const }
    : { tailMessages: dbTailMessages, source: 'db' as const };
}

function extractAssistantTextInfo(messages: UIMessage[]) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant') continue;
    const parts = (msg as any).parts ?? [];
    let fullText = '';
    let lastTextPartIndex = -1;
    let lastTextPartText = '';
    for (let j = 0; j < parts.length; j++) {
      if (parts[j]?.type === 'text') {
        fullText += parts[j].text ?? '';
        lastTextPartIndex = j;
        lastTextPartText = parts[j].text ?? '';
      }
    }
    return {
      fullText,
      messageIndex: i,
      lastTextPartIndex,
      lastTextPartText,
      partsCount: parts.length,
    };
  }
  return null;
}

function getLatestRedisCursor(data: any[] | undefined) {
  if (!Array.isArray(data)) return null;

  for (let index = data.length - 1; index >= 0; index -= 1) {
    const item = data[index] as any;
    if (
      item?.type === 'redis_chunk_id' &&
      typeof item?.content === 'string' &&
      typeof item?.request_id === 'string'
    ) {
      return {
        chunkId: item.content,
        requestId: item.request_id,
      };
    }
  }

  return null;
}

async function consumeResumeStream({
  stream,
  lastMessage,
  onUpdate,
}: {
  stream: ReadableStream<Uint8Array>;
  lastMessage?: UIMessage;
  onUpdate: (payload: {
    message: UIMessage;
    data: any[];
    replaceLastMessage: boolean;
  }) => void;
}) {
  const replaceLastMessage = lastMessage?.role === 'assistant';
  let step = replaceLastMessage
    ? 1 +
      (((lastMessage as any)?.toolInvocations ?? []).reduce(
        (maxStep: number, invocation: any) =>
          Math.max(maxStep, invocation?.step ?? 0),
        0,
      ) ?? 0)
    : 0;

  const message: any = replaceLastMessage
    ? structuredClone(lastMessage)
    : {
        id: generateUUID(),
        createdAt: new Date(),
        role: 'assistant',
        content: '',
        parts: [],
      };

  if (!Array.isArray(message.parts)) {
    message.parts = [];
  }

  let currentTextPart: any;
  let currentReasoningPart: any;
  let currentReasoningTextDetail: any;

  if (replaceLastMessage) {
    const trailingPart = message.parts[message.parts.length - 1];
    if (trailingPart?.type === 'text') {
      currentTextPart = trailingPart;
    }
    if (trailingPart?.type === 'reasoning') {
      currentReasoningPart = trailingPart;
      const trailingDetail = Array.isArray(trailingPart.details)
        ? trailingPart.details[trailingPart.details.length - 1]
        : undefined;
      if (trailingDetail?.type === 'text') {
        currentReasoningTextDetail = trailingDetail;
      }
    }
  }

  const data: any[] = [];
  let messageAnnotations = replaceLastMessage
    ? (lastMessage as any)?.annotations
    : undefined;

  function updateToolInvocationPart(toolCallId: string, invocation: any) {
    const partIndex = message.parts.findIndex(
      (part: any) =>
        part?.type === 'tool-invocation' &&
        part?.toolInvocation?.toolCallId === toolCallId,
    );

    if (partIndex >= 0) {
      message.parts[partIndex].toolInvocation = invocation;
      return;
    }

    message.parts.push({
      type: 'tool-invocation',
      toolInvocation: invocation,
    });
  }

  function execUpdate() {
    const copiedMessage: UIMessage = {
      ...structuredClone(message),
      revisionId: generateUUID(),
    } as UIMessage;

    if (messageAnnotations?.length) {
      (copiedMessage as any).annotations = messageAnnotations;
    }

    onUpdate({
      message: copiedMessage,
      data: [...data],
      replaceLastMessage,
    });
  }

  await processDataStream({
    stream,
    onTextPart(value) {
      if (currentTextPart == null) {
        currentTextPart = {
          type: 'text',
          text: value,
        };
        message.parts.push(currentTextPart);
      } else {
        currentTextPart.text += value;
      }

      message.content = `${message.content ?? ''}${value}`;
      execUpdate();
    },
    onReasoningPart(value) {
      if (currentReasoningTextDetail == null) {
        currentReasoningTextDetail = { type: 'text', text: value };
        if (currentReasoningPart != null) {
          currentReasoningPart.details.push(currentReasoningTextDetail);
        }
      } else {
        currentReasoningTextDetail.text += value;
      }

      if (currentReasoningPart == null) {
        currentReasoningPart = {
          type: 'reasoning',
          reasoning: value,
          details: [currentReasoningTextDetail],
        };
        message.parts.push(currentReasoningPart);
      } else {
        currentReasoningPart.reasoning += value;
      }

      message.reasoning = `${message.reasoning ?? ''}${value}`;
      execUpdate();
    },
    onReasoningSignaturePart(value) {
      if (currentReasoningTextDetail != null) {
        currentReasoningTextDetail.signature = value.signature;
      }
    },
    onRedactedReasoningPart(value) {
      if (currentReasoningPart == null) {
        currentReasoningPart = {
          type: 'reasoning',
          reasoning: '',
          details: [],
        };
        message.parts.push(currentReasoningPart);
      }

      currentReasoningPart.details.push({
        type: 'redacted',
        data: value.data,
      });
      currentReasoningTextDetail = undefined;
      execUpdate();
    },
    onSourcePart(value) {
      message.parts.push({
        type: 'source',
        source: value,
      });
      execUpdate();
    },
    onFilePart(value) {
      message.parts.push({
        type: 'file',
        mimeType: value.mimeType,
        data: value.data,
      });
      execUpdate();
    },
    onDataPart(value) {
      data.push(...value);
      execUpdate();
    },
    onMessageAnnotationsPart(value) {
      if (messageAnnotations == null) {
        messageAnnotations = [...value];
      } else {
        messageAnnotations.push(...value);
      }
      execUpdate();
    },
    onToolCallPart(value) {
      const invocation = {
        state: 'call',
        step,
        ...value,
      };

      if (!Array.isArray(message.toolInvocations)) {
        message.toolInvocations = [];
      }

      const invocationIndex = message.toolInvocations.findIndex(
        (toolInvocation: any) =>
          toolInvocation?.toolCallId === value.toolCallId,
      );

      if (invocationIndex >= 0) {
        message.toolInvocations[invocationIndex] = invocation;
      } else {
        message.toolInvocations.push(invocation);
      }

      updateToolInvocationPart(value.toolCallId, invocation);
      execUpdate();
    },
    onToolResultPart(value) {
      if (!Array.isArray(message.toolInvocations)) {
        message.toolInvocations = [];
      }

      const invocationIndex = message.toolInvocations.findIndex(
        (toolInvocation: any) =>
          toolInvocation?.toolCallId === value.toolCallId,
      );

      const previousInvocation =
        invocationIndex >= 0 ? message.toolInvocations[invocationIndex] : {};
      const invocation = {
        ...previousInvocation,
        state: 'result',
        ...value,
      };

      if (invocationIndex >= 0) {
        message.toolInvocations[invocationIndex] = invocation;
      } else {
        message.toolInvocations.push(invocation);
      }

      updateToolInvocationPart(value.toolCallId, invocation);
      execUpdate();
    },
    onFinishStepPart(value) {
      step += 1;
      currentTextPart = value.isContinued ? currentTextPart : undefined;
      currentReasoningPart = undefined;
      currentReasoningTextDetail = undefined;
    },
    onStartStepPart(value) {
      if (!replaceLastMessage) {
        message.id = value.messageId;
      }
      message.parts.push({ type: 'step-start' });
      execUpdate();
    },
    onFinishMessagePart() {},
    onErrorPart(error) {
      throw new Error(error);
    },
  });
}

export function useAutoResume({
  autoResume,
  chatId,
  userId,
  replayId,
  messages,
  status,
  data,
  setData,
  setMessages,
  onRecoveryFailed,
  skipDbHydrationRef,
}: UseAutoResumeParams): UseAutoResumeResult {
  // Small helper so every DB-hydration setMessages goes through the same
  // guard. When the host has an optimistic append in-flight, we refuse to
  // clobber the chat state. The host clears the ref when the stream arrives.
  const safeHydrateFromDb = (
    msgs: UIMessage[],
    callSite: string,
  ): boolean => {
    if (skipDbHydrationRef?.current) {
      // eslint-disable-next-line no-console
      console.info(
        `[use-auto-resume] DB hydration skipped (callSite=${callSite}) — optimistic append in-flight`,
      );
      autoResumeHandoffLog('auto_resume:db_hydration:skipped', {
        callSite,
        chatId,
        incomingMessageCount: msgs.length,
      });
      return false;
    }
    // Log when we DO hydrate (this is the normal, non-handoff path).
    // Helps prove the guard isn't accidentally firing for regular sessions.
    autoResumeHandoffLog('auto_resume:db_hydration:applied', {
      callSite,
      chatId,
      incomingMessageCount: msgs.length,
      hasSkipRef: skipDbHydrationRef !== undefined,
    });
    setMessages(msgs);
    return true;
  };
  const [recoveryMode, setRecoveryMode] = useState<RecoveryMode>('idle');
  const [manualRecoveryKey, setManualRecoveryKey] = useState(0);
  const resumeAbortRef = useRef<AbortController | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollWaitResolveRef = useRef<(() => void) | null>(null);
  const activeRequestIdRef = useRef<string | null>(null);
  const lastChunkIdRef = useRef<string | null>(null);
  const hasRecoveredRef = useRef(false);
  const lastManualRecoveryKeyRef = useRef(0);
  const nextRecoveryTriggerRef = useRef<'mount' | 'live_drop'>('mount');
  const lastPollHashRef = useRef<string>('');
  const pollDripTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusRef = useRef(status);
  statusRef.current = status;
  const recoveryTelemetryRef = useRef<RecoveryTelemetryState>(
    createRecoveryTelemetryState(),
  );
  const resumeDataIndexRef = useRef(0);

  const resetRecoveryTelemetry = useCallback((attempt = 0) => {
    recoveryTelemetryRef.current = createRecoveryTelemetryState(attempt);
    resumeDataIndexRef.current = 0;
  }, []);

  const emitRecoveryEvent = useCallback(
    (
      eventType: Parameters<typeof telemetry.emit>[0],
      payload: Record<string, unknown> = {},
    ) => {
      telemetry.emit(eventType, {
        session_id: chatId,
        user_id: userId,
        request_id:
          (payload.request_id as string | undefined) ??
          recoveryTelemetryRef.current.requestId ??
          undefined,
        recovery_attempt: recoveryTelemetryRef.current.attempt,
        trigger: recoveryTelemetryRef.current.trigger ?? undefined,
        recovery_version: 'v1',
        ...payload,
      });
    },
    [chatId, userId],
  );

  // Reset hasRecoveredRef when chatId changes (user navigates to different chat)
  useEffect(() => {
    hasRecoveredRef.current = false;
    lastManualRecoveryKeyRef.current = 0;
    nextRecoveryTriggerRef.current = 'mount';
    lastPollHashRef.current = '';
    resetRecoveryTelemetry();
  }, [chatId, resetRecoveryTelemetry]);

  useEffect(() => {
    const latestCursor = getLatestRedisCursor(data as any[] | undefined);
    if (!latestCursor) return;

    activeRequestIdRef.current = latestCursor.requestId;
    lastChunkIdRef.current = latestCursor.chunkId;
  }, [data]);

  useEffect(() => {
    const requestId = activeRequestIdRef.current;
    const lastChunkId = lastChunkIdRef.current;
    const shouldPersist =
      status === 'submitted' || status === 'streaming' || recoveryMode === 'streaming';

    if (!requestId || !lastChunkId || !shouldPersist) {
      return;
    }

    const tailMessages = sliceRequestTail(messages, requestId);
    if (tailMessages.length === 0) {
      return;
    }

    writeStoredResumeState(chatId, {
      requestId,
      lastChunkId,
      tailMessages,
      updatedAt: Date.now(),
    });
  }, [chatId, messages, recoveryMode, status]);

  // Cancel recovery if the main useChat stream starts (user sends a new message)
  useEffect(() => {
    if (
      (status === 'submitted' || status === 'streaming') &&
      recoveryMode !== 'idle'
    ) {
      const recovery = recoveryTelemetryRef.current;
      if (recovery.startedAtMs && recovery.startedAtIso) {
        emitRecoveryEvent('stream.recovery_cancelled', {
          mode: recoveryMode,
          reason: 'new_live_stream_started',
          started_at_client_iso: recovery.startedAtIso,
          duration_ms: Date.now() - recovery.startedAtMs,
        });
      }
      resumeAbortRef.current?.abort();
      pollWaitResolveRef.current?.();
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      if (pollDripTimerRef.current) {
        clearTimeout(pollDripTimerRef.current);
        pollDripTimerRef.current = null;
      }
      setRecoveryMode('idle');
      resetRecoveryTelemetry(recovery.attempt);
    }
  }, [emitRecoveryEvent, recoveryMode, resetRecoveryTelemetry, status]);

  useEffect(() => {
    const manualTrigger = manualRecoveryKey > lastManualRecoveryKeyRef.current;
    if (
      !autoResume ||
      replayId ||
      (!manualTrigger &&
        (statusRef.current === 'submitted' || statusRef.current === 'streaming'))
    ) {
      // Gate beacon (July 2026): only for explicit recovery requests — the
      // non-manual mount path returns here on every normal streaming chat and
      // would be pure noise. A manual trigger dying here (e.g. autoResume
      // false) was previously invisible.
      if (manualTrigger) {
        lastManualRecoveryKeyRef.current = manualRecoveryKey;
        emitRecoveryEvent('stream.recovery_gated', {
          gate: 'effect_entry',
          auto_resume: autoResume,
          is_replay: Boolean(replayId),
          status: statusRef.current,
        });
      }
      return;
    }

    if (manualTrigger) {
      lastManualRecoveryKeyRef.current = manualRecoveryKey;
    } else {
      // Only attempt recovery once per chat load to prevent restart loops
      if (hasRecoveredRef.current) return;
      hasRecoveredRef.current = true;
    }

    let cancelled = false;
    const recoveryTrigger = manualTrigger
      ? nextRecoveryTriggerRef.current
      : 'mount';

    const abortController = new AbortController();
    resumeAbortRef.current = abortController;
    const isCancelled = () => cancelled || abortController.signal.aborted;

    const fetchRequestStatus = async (
      requestId?: string,
      phase: 'initial' | 'poll_loop' | 'poll_error_recheck' = 'initial',
    ) => {
      const startedAtMs = Date.now();
      const url = new URL(`${API_BASE_URL}/agent/request-status`);
      url.searchParams.set('session_id', chatId);
      if (requestId) {
        url.searchParams.set('request_id', requestId);
      }

      try {
        // HARD TIMEOUT (July 2026, session 7313695e deep-dive): this fetch
        // had no timeout. When the browser's connection to the backend was
        // dead (the exact situation recovery runs in), the promise never
        // settled — recover() hung forever with zero telemetry and the user
        // saw nothing until they manually re-asked. A timed-out probe now
        // throws, which routes into the existing auto-rehydrate fallback
        // (recover().catch) that loads the saved answer from the DB.
        const response = await fetch(url.toString(), {
          credentials: 'include',
          signal: AbortSignal.timeout(10000),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch request status: ${response.status}`);
        }

        const payload = (await response.json()) as RequestStatusResponse;
        if (phase !== 'poll_loop') {
          emitRecoveryEvent('stream.resume_status', {
            request_id: payload.request_id ?? requestId ?? undefined,
            request_id_input: requestId ?? null,
            phase,
            status: payload.status,
            latency_ms: Date.now() - startedAtMs,
          });
        }
        return payload;
      } catch (error) {
        if (phase !== 'poll_loop') {
          const details = describeError(error);
          emitRecoveryEvent('stream.resume_status', {
            request_id: requestId ?? undefined,
            request_id_input: requestId ?? null,
            phase,
            status: 'error',
            latency_ms: Date.now() - startedAtMs,
            ...details,
          });
        }
        throw error;
      }
    };

    const fetchDbMessages = async () => {
      const session = await sessionFetcher(chatId, userId);
      return convertToUIMessages(session.messages || []);
    };

    const finalizeRecovery = async (
      result: string,
      extra: Record<string, unknown> = {},
    ) => {
      let finalMessageCount: number | null = null;
      let hydrationError: ReturnType<typeof describeError> | null = null;
      try {
        const finalMessages = await fetchDbMessages();
        finalMessageCount = finalMessages.length;
        if (!isCancelled()) {
          setMessages(finalMessages);
        }
      } catch (error) {
        hydrationError = describeError(error);
      }

      const recovery = recoveryTelemetryRef.current;
      if (recovery.startedAtMs && recovery.startedAtIso) {
        emitRecoveryEvent('stream.recovery_complete', {
          result,
          mode: recovery.mode,
          used_polling_fallback: recovery.usedPollingFallback,
          fallback_reason: recovery.fallbackReason,
          started_at_client_iso: recovery.startedAtIso,
          duration_ms: Date.now() - recovery.startedAtMs,
          final_message_count: finalMessageCount,
          resume_chunk_count: recovery.resumeChunkOffsetsMs.length,
          resume_chunk_offsets_ms: recovery.resumeChunkOffsetsMs,
          resume_heartbeat_offsets_ms: recovery.resumeHeartbeatOffsetsMs,
          max_resume_gap_ms: recovery.maxResumeGapMs,
          time_to_first_resume_chunk_ms: recovery.timeToFirstResumeChunkMs,
          poll_iterations: recovery.pollIterations,
          poll_updates: recovery.pollUpdates,
          poll_progress_offsets_ms: recovery.pollProgressOffsetsMs,
          poll_message_counts: recovery.pollMessageCounts,
          poll_assistant_text_lengths: recovery.pollAssistantTextLengths,
          hydrate_error_type: hydrationError?.error_type,
          hydrate_error_message: hydrationError?.error_message,
          ...extra,
        });
        // Metric reclassification (Aug 2026): a live drop that recovery fixed
        // is a save, not a failure. Pairs with stream.interrupted from
        // chat.tsx so dashboards can compute save rate without joins.
        if (
          recovery.trigger === 'live_drop' &&
          !hydrationError &&
          result !== 'error'
        ) {
          emitRecoveryEvent('stream.recovered', {
            result,
            mode: recovery.mode,
            duration_ms: Date.now() - recovery.startedAtMs,
          });
        }
      } else if (Object.keys(extra).length > 0 || result !== 'skipped') {
        emitRecoveryEvent('stream.recovery_complete', {
          result,
          final_message_count: finalMessageCount,
          hydrate_error_type: hydrationError?.error_type,
          hydrate_error_message: hydrationError?.error_message,
          ...extra,
        });
      }

      clearStoredResumeState(chatId);
      activeRequestIdRef.current = null;
      lastChunkIdRef.current = null;
      resetRecoveryTelemetry(recovery.attempt);

      if (!isCancelled()) {
        setRecoveryMode('idle');
      }
    };

    const computePollHash = (msgs: UIMessage[]) => {
      const last = msgs[msgs.length - 1];
      if (!last) return '0';
      const partsLen = JSON.stringify(last.parts ?? []).length;
      return `${msgs.length}:${last.id}:${partsLen}:${(last.content ?? '').length}`;
    };

    const startPollingFallback = async (requestId: string) => {
      const recovery = recoveryTelemetryRef.current;
      recovery.mode = 'polling';
      recovery.requestId = requestId;
      if (!recovery.pollStartedAtMs) {
        recovery.pollStartedAtMs = Date.now();
        recovery.pollStartedAtIso = new Date(
          recovery.pollStartedAtMs,
        ).toISOString();
      }

      emitRecoveryEvent('stream.poll_start', {
        request_id: requestId,
        started_at_client_iso: recovery.pollStartedAtIso,
        used_polling_fallback: recovery.usedPollingFallback,
        fallback_reason: recovery.fallbackReason,
      });

      setRecoveryMode('polling');
      setData([]);
      lastPollHashRef.current = '';

      let displayedMessages: UIMessage[] = [];
      let dripAborted = false;

      const triggerImmediatePoll = () => {
        pollWaitResolveRef.current?.();
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
        pollWaitResolveRef.current = null;
      };

      // 2026-07-10: hidden tabs get their timers throttled hard by the
      // browser, so a backgrounded poll loop can crawl. The moment the user
      // returns, check the DB immediately instead of waiting out whatever is
      // left of the (possibly throttled) interval — the finished answer then
      // appears the instant the tab is visible again.
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          triggerImmediatePoll();
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);

      const cancelDrip = () => {
        if (pollDripTimerRef.current !== null) {
          clearTimeout(pollDripTimerRef.current);
          pollDripTimerRef.current = null;
        }
        dripAborted = true;
      };

      // try/finally so the visibilitychange listener is removed on EVERY exit
      // path (timeout return, completion return, cancel return, throw).
      try {
      while (!isCancelled()) {
        recoveryTelemetryRef.current.pollIterations += 1;

        // Risk B: bail out if we've been polling past the hard budget. The
        // request is almost certainly stuck server-side; finalize from the DB
        // snapshot rather than looping forever.
        const pollElapsedMs = recovery.pollStartedAtMs
          ? Date.now() - recovery.pollStartedAtMs
          : 0;
        if (pollElapsedMs > MAX_POLL_DURATION_MS) {
          cancelDrip();
          emitRecoveryEvent('stream.poll_timeout', {
            request_id: requestId,
            duration_ms: pollElapsedMs,
            poll_iterations: recovery.pollIterations,
            poll_updates: recovery.pollUpdates,
            recovery_version: 'v2',
          });
          await finalizeRecovery('poll_timeout', {
            final_source: 'polling',
            status_at_completion: 'poll_timeout',
            recovery_version: 'v2',
          });
          return;
        }

        try {
          const dbMessages = await fetchDbMessages();
          if (isCancelled()) { cancelDrip(); return; }

          const hash = computePollHash(dbMessages);
          if (hash !== lastPollHashRef.current) {
            lastPollHashRef.current = hash;

            const oldInfo = extractAssistantTextInfo(displayedMessages);
            const newInfo = extractAssistantTextInfo(dbMessages);

            const canAnimate =
              oldInfo != null &&
              newInfo != null &&
              oldInfo.messageIndex === newInfo.messageIndex &&
              displayedMessages.length === dbMessages.length &&
              oldInfo.partsCount === newInfo.partsCount &&
              newInfo.lastTextPartIndex >= 0 &&
              newInfo.fullText.startsWith(oldInfo.fullText) &&
              newInfo.fullText.length > oldInfo.fullText.length &&
              (newInfo.fullText.length - oldInfo.fullText.length) <=
                newInfo.lastTextPartText.length;

            const recovery = recoveryTelemetryRef.current;
            const pollStartedAtMs = recovery.pollStartedAtMs ?? Date.now();
            const offsetMs = Date.now() - pollStartedAtMs;
            recovery.pollUpdates += 1;
            recovery.pollProgressOffsetsMs.push(offsetMs);
            recovery.pollMessageCounts.push(dbMessages.length);
            recovery.pollAssistantTextLengths.push(
              newInfo?.fullText.length ?? getAssistantTextLength(dbMessages),
            );
            emitRecoveryEvent('stream.poll_progress', {
              request_id: requestId,
              offset_ms: offsetMs,
              message_count: dbMessages.length,
              assistant_text_len:
                newInfo?.fullText.length ?? getAssistantTextLength(dbMessages),
              animated: canAnimate,
            });

            if (canAnimate) {
              cancelDrip();
              dripAborted = false;

              const delta = newInfo.fullText.slice(oldInfo.fullText.length);
              const words = delta.match(/\S+\s*/g) || [delta];
              let wordIndex = 0;

              const msgIdx = newInfo.messageIndex;
              const partIdx = newInfo.lastTextPartIndex;
              const targetPartText = newInfo.lastTextPartText;

              const tick = () => {
                if (isCancelled() || dripAborted || wordIndex >= words.length) {
                  displayedMessages = dbMessages;
                  if (!isCancelled() && !dripAborted) {
                    setMessages(dbMessages);
                    // Trigger immediate next poll to eliminate dead time
                    triggerImmediatePoll();
                  }
                  pollDripTimerRef.current = null;
                  return;
                }

                wordIndex++;
                const shown = words.slice(0, wordIndex).join('');
                const remaining = delta.length - shown.length;
                const intermediatePartText = targetPartText.slice(
                  0,
                  targetPartText.length - remaining,
                );

                const intermediate = dbMessages.map((msg, mi) => {
                  if (mi !== msgIdx) return msg;
                  const parts = [...((msg as any).parts ?? [])];
                  parts[partIdx] = {
                    ...parts[partIdx],
                    text: intermediatePartText,
                  };
                  return { ...msg, parts } as UIMessage;
                });

                displayedMessages = intermediate;
                if (!isCancelled()) {
                  setMessages(intermediate);
                }

                if (
                  wordIndex < words.length &&
                  !isCancelled() &&
                  !dripAborted
                ) {
                  pollDripTimerRef.current = setTimeout(
                    tick,
                    DRIP_WORD_INTERVAL_MS,
                  );
                } else {
                  displayedMessages = dbMessages;
                  if (!isCancelled() && !dripAborted) {
                    // Trigger immediate next poll when animation completes naturally
                    triggerImmediatePoll();
                  }
                  pollDripTimerRef.current = null;
                }
              };

              tick();
            } else {
              cancelDrip();
              displayedMessages = dbMessages;
              setMessages(dbMessages);
            }
          }

          const requestInfo = await fetchRequestStatus(requestId);
          if (isCancelled()) { cancelDrip(); return; }

          if (requestInfo.status !== 'running') {
            cancelDrip();
            const recovery = recoveryTelemetryRef.current;
            emitRecoveryEvent('stream.poll_complete', {
              request_id: requestId,
              status: requestInfo.status,
              duration_ms: recovery.pollStartedAtMs
                ? Date.now() - recovery.pollStartedAtMs
                : undefined,
              poll_iterations: recovery.pollIterations,
              poll_updates: recovery.pollUpdates,
              progress_offsets_ms: recovery.pollProgressOffsetsMs,
              message_counts: recovery.pollMessageCounts,
              assistant_text_lengths: recovery.pollAssistantTextLengths,
            });
            await finalizeRecovery('ok', {
              final_source: 'polling',
              status_at_completion: requestInfo.status,
            });
            return;
          }
        } catch {
          const requestInfo = await fetchRequestStatus(
            requestId,
            'poll_error_recheck',
          ).catch(() => null);
          if (isCancelled()) { cancelDrip(); return; }
          if (!requestInfo || requestInfo.status !== 'running') {
            cancelDrip();
            const recovery = recoveryTelemetryRef.current;
            emitRecoveryEvent('stream.poll_complete', {
              request_id: requestId,
              status: requestInfo?.status ?? 'request_status_unavailable',
              duration_ms: recovery.pollStartedAtMs
                ? Date.now() - recovery.pollStartedAtMs
                : undefined,
              poll_iterations: recovery.pollIterations,
              poll_updates: recovery.pollUpdates,
              progress_offsets_ms: recovery.pollProgressOffsetsMs,
              message_counts: recovery.pollMessageCounts,
              assistant_text_lengths: recovery.pollAssistantTextLengths,
            });
            await finalizeRecovery('ok', {
              final_source: 'polling',
              status_at_completion:
                requestInfo?.status ?? 'request_status_unavailable',
            });
            return;
          }
        }

        await new Promise<void>((resolve) => {
          const completeWait = () => {
            pollTimeoutRef.current = null;
            pollWaitResolveRef.current = null;
            resolve();
          };

          pollWaitResolveRef.current = completeWait;
          // Gentle backoff: 2s for the first 5 minutes (snappy recovery for the
          // common case), 5s afterwards (a 30-min zombie watch shouldn't hammer
          // the sessions endpoint with 900 full fetches).
          const elapsed = recovery.pollStartedAtMs
            ? Date.now() - recovery.pollStartedAtMs
            : 0;
          const interval = elapsed > 300_000 ? 5000 : POLL_INTERVAL_MS;
          pollTimeoutRef.current = setTimeout(completeWait, interval);
        });
      }

      cancelDrip();
      } finally {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };

    const startStreamingResume = async (
      requestId: string,
      lastChunkId: string,
      hydratedMessages: UIMessage[],
    ) => {
      const recovery = recoveryTelemetryRef.current;
      recovery.mode = 'streaming';
      recovery.requestId = requestId;
      resumeDataIndexRef.current = 0;

      setRecoveryMode('streaming');
      setData([]);
      const resumeBaseMessages = [...hydratedMessages];
      const resumeStartedAtMs = Date.now();

      try {
        const response = await fetchWithErrorHandlers(
          `${API_BASE_URL}/agent/stream-resume`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              session_id: chatId,
              request_id: requestId,
              last_chunk_id: lastChunkId,
            }),
            signal: abortController.signal,
          },
        );

        if (!response.body) {
          throw new Error('Resume response body is empty.');
        }

        await consumeResumeStream({
          stream: response.body,
          lastMessage:
            hydratedMessages[hydratedMessages.length - 1]?.role === 'assistant'
              ? hydratedMessages[hydratedMessages.length - 1]
              : undefined,
          onUpdate: ({ message, data: nextData, replaceLastMessage }) => {
            for (
              let index = resumeDataIndexRef.current;
              index < nextData.length;
              index += 1
            ) {
              const item = nextData[index] as any;
              if (
                item?.type === 'redis_chunk_id' &&
                typeof item?.content === 'string' &&
                item.content !== recoveryTelemetryRef.current.lastResumeChunkId
              ) {
                const nowMs = Date.now();
                const telemetryState = recoveryTelemetryRef.current;
                if (
                  telemetryState.lastResumeActivityAtMs !== null &&
                  telemetryState.startedAtMs
                ) {
                  telemetryState.maxResumeGapMs = Math.max(
                    telemetryState.maxResumeGapMs,
                    nowMs - telemetryState.lastResumeActivityAtMs,
                  );
                }
                telemetryState.lastResumeActivityAtMs = nowMs;
                telemetryState.lastResumeChunkId = item.content;
                if (telemetryState.startedAtMs) {
                  const offsetMs = nowMs - telemetryState.startedAtMs;
                  telemetryState.resumeChunkOffsetsMs.push(offsetMs);
                  if (telemetryState.timeToFirstResumeChunkMs === null) {
                    telemetryState.timeToFirstResumeChunkMs = offsetMs;
                  }
                }
              } else if (
                item?.type === 'heartbeat' &&
                item?.source === 'stream_route_keepalive'
              ) {
                const nowMs = Date.now();
                const telemetryState = recoveryTelemetryRef.current;
                if (
                  telemetryState.lastResumeActivityAtMs !== null &&
                  telemetryState.startedAtMs
                ) {
                  telemetryState.maxResumeGapMs = Math.max(
                    telemetryState.maxResumeGapMs,
                    nowMs - telemetryState.lastResumeActivityAtMs,
                  );
                }
                telemetryState.lastResumeActivityAtMs = nowMs;
                if (telemetryState.startedAtMs) {
                  telemetryState.resumeHeartbeatOffsetsMs.push(
                    nowMs - telemetryState.startedAtMs,
                  );
                }
              }
            }
            resumeDataIndexRef.current = nextData.length;

            const latestCursor = getLatestRedisCursor(nextData);
            if (latestCursor) {
              activeRequestIdRef.current = latestCursor.requestId;
              lastChunkIdRef.current = latestCursor.chunkId;
            }

            setData(nextData as any);
            setMessages(
              replaceLastMessage
                ? [...resumeBaseMessages.slice(0, -1), message]
                : [...resumeBaseMessages, message],
            );
          },
        });

        telemetry.streamResumeComplete({
          session_id: chatId,
          user_id: userId,
          request_id: requestId,
          duration_ms: Date.now() - resumeStartedAtMs,
          chunks_processed: recovery.resumeChunkOffsetsMs.length,
          chunks_deduped: 0,
          outcome:
            recovery.resumeChunkOffsetsMs.length > 0 ? 'ok' : 'empty',
          chunk_offsets_ms: recovery.resumeChunkOffsetsMs,
          heartbeat_offsets_ms: recovery.resumeHeartbeatOffsetsMs,
          max_chunk_gap_ms: recovery.maxResumeGapMs,
          time_to_first_chunk_ms: recovery.timeToFirstResumeChunkMs,
        });
      } catch (error) {
        const details = describeError(error);
        telemetry.streamResumeComplete({
          session_id: chatId,
          user_id: userId,
          request_id: requestId,
          duration_ms: Date.now() - resumeStartedAtMs,
          chunks_processed: recovery.resumeChunkOffsetsMs.length,
          chunks_deduped: 0,
          outcome: 'error',
          chunk_offsets_ms: recovery.resumeChunkOffsetsMs,
          heartbeat_offsets_ms: recovery.resumeHeartbeatOffsetsMs,
          max_chunk_gap_ms: recovery.maxResumeGapMs,
          time_to_first_chunk_ms: recovery.timeToFirstResumeChunkMs,
          ...details,
        });
        throw error;
      }
    };

    const recover = async () => {
      recoveryTelemetryRef.current.trigger = recoveryTrigger;
      // Entry beacon (July 2026, session 7313695e deep-dive): ~50 of 366
      // error_routed_to_recovery events in 14 days were followed by ZERO
      // recovery telemetry, and without an entry event we can't tell whether
      // recover() never ran (gated) or ran and hung on the first fetch.
      // This emit is the first observable artifact inside recover().
      emitRecoveryEvent('stream.recovery_started', {
        manual_trigger: manualTrigger,
        recovery_trigger: recoveryTrigger,
      });
      const requestInfo = await fetchRequestStatus(undefined, 'initial');
      if (isCancelled()) return;

      if (requestInfo.status !== 'running' || !requestInfo.request_id) {
        try {
          const finalMessages = await fetchDbMessages();
          if (!isCancelled()) {
            // Skip if an optimistic append is in-flight. Overwriting here
            // would wipe the user's just-typed message (the edit_alert
            // handoff wipe bug).
            safeHydrateFromDb(finalMessages, 'recover:skipped_path');
          }
        } catch {
          // Best-effort DB refresh only; keep existing skip behavior on failure.
        }
        clearStoredResumeState(chatId);
        activeRequestIdRef.current = null;
        lastChunkIdRef.current = null;
        emitRecoveryEvent('stream.recovery_complete', {
          request_id: requestInfo.request_id ?? undefined,
          result: 'skipped',
          status_at_completion: requestInfo.status,
        });
        setRecoveryMode('idle');
        return;
      }

      const nextAttempt = recoveryTelemetryRef.current.attempt + 1;
      resetRecoveryTelemetry(nextAttempt);
      recoveryTelemetryRef.current.requestId = requestInfo.request_id;
      recoveryTelemetryRef.current.startedAtMs = Date.now();
      recoveryTelemetryRef.current.startedAtIso = new Date(
        recoveryTelemetryRef.current.startedAtMs,
      ).toISOString();
      recoveryTelemetryRef.current.lastResumeActivityAtMs =
        recoveryTelemetryRef.current.startedAtMs;
      activeRequestIdRef.current = requestInfo.request_id;

      const dbMessages = await fetchDbMessages();
      if (isCancelled()) return;

      const storedState = readStoredResumeState(chatId);
      const dbTailMessages = sliceRequestTail(dbMessages, requestInfo.request_id);

      let hydratedMessages = dbMessages;
      let canResumeFromCursor = false;
      let resumeFromChunkId: string | null = null;

      if (
        storedState?.requestId === requestInfo.request_id &&
        storedState.lastChunkId
      ) {
        const preferredTail = choosePreferredTail(
          dbTailMessages,
          storedState.tailMessages,
        );

        hydratedMessages = mergeRequestTail(
          dbMessages,
          requestInfo.request_id,
          preferredTail.tailMessages,
        );
        // Always attempt streaming resume when we have a valid cursor,
        // regardless of which tail was preferred for initial hydration
        canResumeFromCursor = true;
        resumeFromChunkId = storedState.lastChunkId;
      }

      telemetry.streamResumeStart({
        session_id: chatId,
        user_id: userId,
        request_id: requestInfo.request_id,
        cursor: resumeFromChunkId,
        trigger: recoveryTrigger,
        started_at_client_iso: recoveryTelemetryRef.current.startedAtIso,
        stored_state_age_ms: storedState
          ? Date.now() - storedState.updatedAt
          : null,
      });

      // Same guard as above — don't clobber an optimistic append.
      safeHydrateFromDb(hydratedMessages, 'recover:resume_hydrate');

      if (FORCE_POLLING_FALLBACK || !canResumeFromCursor || !resumeFromChunkId) {
        emitRecoveryEvent('stream.resume_decision', {
          request_id: requestInfo.request_id,
          mode: 'polling',
          reason: FORCE_POLLING_FALLBACK
            ? 'forced_polling_fallback'
            : 'missing_resume_cursor',
          had_stored_state: Boolean(storedState),
          hydrated_message_count: hydratedMessages.length,
          db_tail_message_count: dbTailMessages.length,
        });
        await startPollingFallback(requestInfo.request_id);
        return;
      }

      emitRecoveryEvent('stream.resume_decision', {
        request_id: requestInfo.request_id,
        mode: 'streaming',
        reason: 'resume_from_cursor',
        had_stored_state: Boolean(storedState),
        hydrated_message_count: hydratedMessages.length,
        db_tail_message_count: dbTailMessages.length,
        resume_from_chunk_id: resumeFromChunkId,
      });

      try {
        await startStreamingResume(
          requestInfo.request_id,
          resumeFromChunkId,
          hydratedMessages,
        );
        if (!isCancelled()) {
          await finalizeRecovery('ok', {
            final_source: 'stream_resume',
          });
        }
      } catch (error) {
        if (isCancelled()) {
          return;
        }

        const details = describeError(error);
        recoveryTelemetryRef.current.usedPollingFallback = true;
        recoveryTelemetryRef.current.fallbackReason = details.error_message;
        emitRecoveryEvent('stream.resume_fallback', {
          request_id: requestInfo.request_id,
          fallback_to: 'polling',
          resume_from_chunk_id: resumeFromChunkId,
          ...details,
        });
        await startPollingFallback(requestInfo.request_id);
      }
    };

    recover().catch(async (error) => {
      if (isCancelled()) return;
      const details = describeError(error);

      // Fix #5 (reliability v2): the streaming + polling pipeline threw. Before
      // falling back to the manual "tap to reload" button (which users mostly
      // ignore), silently re-load the saved answer from the DB. The backend
      // finishes + persists every request, so this usually surfaces the
      // completed reply with no user action. Bounded retries + backoff guarantee
      // no infinite loop, and skipDbHydrationRef still blocks clobbering an
      // optimistic append.
      let rehydrated = false;
      let lastRehydrateError: ReturnType<typeof describeError> | null = null;
      let finalMessageCount: number | null = null;
      for (
        let attempt = 1;
        attempt <= AUTO_REHYDRATE_MAX_ATTEMPTS && !isCancelled();
        attempt += 1
      ) {
        emitRecoveryEvent('stream.auto_rehydrate_attempt', {
          attempt,
          recovery_version: 'v2',
          ...details,
        });
        try {
          const msgs = await fetchDbMessages();
          if (isCancelled()) return;
          finalMessageCount = msgs.length;
          // safeHydrateFromDb returns false only when an optimistic append is
          // in-flight — in that case the host owns the UI, so treat it as
          // handled (don't show the failure button) and stop.
          const applied = safeHydrateFromDb(msgs, 'recover:auto_rehydrate');
          rehydrated = true;
          emitRecoveryEvent('stream.auto_rehydrate_success', {
            attempt,
            applied,
            final_message_count: finalMessageCount,
            recovery_version: 'v2',
          });
          break;
        } catch (rehydrateError) {
          lastRehydrateError = describeError(rehydrateError);
        }
        if (attempt < AUTO_REHYDRATE_MAX_ATTEMPTS) {
          await new Promise((resolve) =>
            setTimeout(resolve, AUTO_REHYDRATE_BACKOFF_MS * attempt),
          );
        }
      }

      if (isCancelled()) return;

      emitRecoveryEvent('stream.recovery_complete', {
        result: rehydrated ? 'auto_rehydrated' : 'error',
        stage: 'recover',
        auto_rehydrated: rehydrated,
        final_message_count: finalMessageCount,
        recovery_version: 'v2',
        ...details,
      });
      setRecoveryMode('idle');

      if (!rehydrated) {
        // Every silent attempt failed — surface the manual reload button as the
        // last resort (v1 behavior).
        emitRecoveryEvent('stream.auto_rehydrate_exhausted', {
          attempts: AUTO_REHYDRATE_MAX_ATTEMPTS,
          recovery_version: 'v2',
          rehydrate_error_type: lastRehydrateError?.error_type,
          rehydrate_error_message: lastRehydrateError?.error_message,
          ...details,
        });
        onRecoveryFailed?.();
      }
    });

    return () => {
      cancelled = true;
      pollWaitResolveRef.current?.();
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      if (pollDripTimerRef.current) {
        clearTimeout(pollDripTimerRef.current);
        pollDripTimerRef.current = null;
      }
      abortController.abort();
      if (resumeAbortRef.current === abortController) {
        resumeAbortRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoResume,
    chatId,
    manualRecoveryKey,
    replayId,
    setData,
    setMessages,
    userId,
  ]);

  const stopResuming = useCallback(() => {
    const recovery = recoveryTelemetryRef.current;
    if (recovery.startedAtMs && recovery.startedAtIso) {
      emitRecoveryEvent('stream.recovery_cancelled', {
        mode: recovery.mode,
        reason: 'user_stop',
        started_at_client_iso: recovery.startedAtIso,
        duration_ms: Date.now() - recovery.startedAtMs,
      });
    }
    pollWaitResolveRef.current?.();
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (pollDripTimerRef.current) {
      clearTimeout(pollDripTimerRef.current);
      pollDripTimerRef.current = null;
    }
    resumeAbortRef.current?.abort();
    resumeAbortRef.current = null;
    activeRequestIdRef.current = null;
    lastChunkIdRef.current = null;
    clearStoredResumeState(chatId);
    setData([]);
    setRecoveryMode('idle');
    resetRecoveryTelemetry(recovery.attempt);
  }, [chatId, emitRecoveryEvent, resetRecoveryTelemetry, setData]);

  const recoverNow = useCallback(() => {
    if (recoveryMode !== 'idle' || replayId) {
      // Gate beacon (July 2026): a blocked recoverNow used to be completely
      // invisible — one of the candidate explanations for "error routed to
      // recovery, then silence". Now it leaves an artifact.
      emitRecoveryEvent('stream.recovery_gated', {
        gate: 'recover_now',
        recovery_mode: recoveryMode,
        is_replay: Boolean(replayId),
      });
      return;
    }
    nextRecoveryTriggerRef.current = 'live_drop';
    setManualRecoveryKey((value) => value + 1);
  }, [emitRecoveryEvent, recoveryMode, replayId]);

  return {
    isRecovering: recoveryMode === 'polling' || recoveryMode === 'streaming',
    isResumeStreaming: recoveryMode === 'streaming',
    stopResuming,
    recoverNow,
  };
}
